// Learning Scheduler Service - Manages daily vocabulary set assignments
// Now uses A2A with Supabase fallback

import vocabRightDataService from './vocabRightDataService';

class LearningScheduler {
  constructor() {
    this.allSets = [];
    this.totalSets = 0;
    this.isLoaded = false;
    this.loadPromise = null;
  }

  // Load all sets from Supabase
  async loadSets() {
    // Return existing promise if already loading
    if (this.loadPromise) {
      return this.loadPromise;
    }

    // Return immediately if already loaded
    if (this.isLoaded) {
      return Promise.resolve(this.allSets);
    }

    // Create new load promise
    this.loadPromise = (async () => {
      try {
        console.log('Loading vocabulary sets via A2A...');
        
        // Fetch all sets using A2A (with Supabase fallback)
        const data = await vocabRightDataService.getVocabularySets();
        
        // Ensure data is an array
        const setsArray = Array.isArray(data) ? data : [];
        
        // Transform data to match the expected format
        this.allSets = setsArray.map((row, index) => ({
          id: row.id || row.set_id,
          title: row.title,
          titleTraditional: row.titleTraditional || row.title_traditional,
          titleEnglish: row.titleEnglish || row.title_english,
          category: row.category,
          categoryEnglish: row.categoryEnglish || row.category_english,
          color: row.color,
          imageUrl: row.imageUrl || row.image_url,
          words: row.words || [],
          studyMode: row.studyMode || row.study_mode || false,
          testMode: row.testMode || row.test_mode || false,
          originalIndex: index
        }));

        this.totalSets = this.allSets.length;
        this.isLoaded = true;
        
        console.log(`Loaded ${this.totalSets} vocabulary sets via A2A`);
        
        return this.allSets;
      } catch (error) {
        console.error('Error loading vocabulary sets:', error);
        this.loadPromise = null; // Reset promise on error to allow retry
        throw error;
      }
    })();

    return this.loadPromise;
  }

  // Ensure sets are loaded before any operation
  async ensureLoaded() {
    if (!this.isLoaded) {
      await this.loadSets();
    }
    return this.allSets;
  }

  // Get today's assigned sets based on user preferences
  async getTodaySets(userSettings) {
    // Ensure learningSchedule exists with defaults
    const learningSchedule = userSettings.learningSchedule || {
      dailyGoal: 1,
      autoAssign: true
    };
    
    const { dailyGoal, autoAssign } = learningSchedule;
    const today = new Date().toISOString().split('T')[0];
    
    await this.ensureLoaded();
    
    if (!autoAssign) {
      return this.getDefaultTodaySets(dailyGoal);
    }
    
    return this.getSmartAssignedSets(today, dailyGoal, userSettings.progress);
  }

  // Get default sets for today (based on day of year)
  getDefaultTodaySets(dailyGoal = 1) {
    if (!this.isLoaded) {
      throw new Error('Sets not loaded. Call ensureLoaded() first.');
    }
    const today = new Date();
    const dayOfYear = Math.floor((today - new Date(today.getFullYear(), 0, 0)) / (1000 * 60 * 60 * 24));
    
    const sets = [];
    for (let i = 0; i < dailyGoal; i++) {
      const setIndex = (dayOfYear + i) % this.totalSets;
      sets.push({
        ...this.allSets[setIndex],
        originalIndex: setIndex,
        assignmentType: 'default'
      });
    }
    
    return sets;
  }

  // Get smart assigned sets based on user progress
  getSmartAssignedSets(today, dailyGoal, progress) {
    if (!this.isLoaded) {
      throw new Error('Sets not loaded. Call ensureLoaded() first.');
    }
    const completedSets = progress.completedSets || [];
    const calendarData = progress.calendarData || {};
    
    // Get sets that haven't been completed recently
    const availableSets = this.allSets.filter((set, index) => {
      const lastCompleted = this.getLastCompletionDate(set.id, calendarData);
      if (!lastCompleted) return true; // Never completed
      
      const daysSinceCompletion = this.getDaysDifference(today, lastCompleted);
      return daysSinceCompletion >= 7; // Wait at least 7 days before repeating
    });
    
    // If not enough available sets, include some completed ones
    if (availableSets.length < dailyGoal) {
      const recentlyCompleted = this.allSets.filter((set, index) => {
        const lastCompleted = this.getLastCompletionDate(set.id, calendarData);
        if (!lastCompleted) return false;
        
        const daysSinceCompletion = this.getDaysDifference(today, lastCompleted);
        return daysSinceCompletion >= 3; // Include sets completed 3+ days ago
      });
      
      availableSets.push(...recentlyCompleted.slice(0, dailyGoal - availableSets.length));
    }
    
    // Randomly select sets for today
    const shuffled = this.shuffleArray([...availableSets]);
    return shuffled.slice(0, dailyGoal).map((set, index) => ({
      ...set,
      originalIndex: this.allSets.findIndex(s => s.id === set.id),
      assignmentType: 'smart'
    }));
  }

  // Get all sets organized by categories for browsing
  getAllSetsOrganized() {
    if (!this.isLoaded) {
      throw new Error('Sets not loaded. Call ensureLoaded() first.');
    }
    const categories = {};
    
    this.allSets.forEach((set, index) => {
      const category = set.categoryEnglish || set.category;
      if (!categories[category]) {
        categories[category] = [];
      }
      categories[category].push({
        ...set,
        originalIndex: index
      });
    });
    
    return categories;
  }

  // Get sets by category
  getSetsByCategory(category) {
    if (!this.isLoaded) {
      throw new Error('Sets not loaded. Call ensureLoaded() first.');
    }
    return this.allSets.filter(set => 
      set.category === category || set.categoryEnglish === category
    );
  }

  // Get sets with study/test mode support
  getStudyModeSets() {
    if (!this.isLoaded) {
      throw new Error('Sets not loaded. Call ensureLoaded() first.');
    }
    return this.allSets.filter(set => set.studyMode || set.testMode);
  }

  // Get sets by difficulty (based on frequency)
  getSetsByDifficulty(difficulty) {
    if (!this.isLoaded) {
      throw new Error('Sets not loaded. Call ensureLoaded() first.');
    }
    return this.allSets.filter(set => {
      const avgFrequency = this.getAverageFrequency(set);
      switch (difficulty) {
        case 'easy': return avgFrequency === 'high';
        case 'medium': return avgFrequency === 'medium';
        case 'hard': return avgFrequency === 'low';
        default: return true;
      }
    });
  }

  // Get average frequency of words in a set
  getAverageFrequency(set) {
    const frequencies = set.words.map(word => word.frequency);
    const highCount = frequencies.filter(f => f === 'high').length;
    const mediumCount = frequencies.filter(f => f === 'medium').length;
    const lowCount = frequencies.filter(f => f === 'low').length;
    
    if (highCount >= mediumCount && highCount >= lowCount) return 'high';
    if (lowCount >= highCount && lowCount >= mediumCount) return 'low';
    return 'medium';
  }

  // Get last completion date for a set
  getLastCompletionDate(setId, calendarData) {
    const dates = Object.keys(calendarData).filter(date => {
      const data = calendarData[date];
      return data.setId === setId && (data.status === 'DS' || data.status === 'DF');
    });
    
    if (dates.length === 0) return null;
    
    return dates.sort().pop(); // Return most recent date
  }

  // Calculate days difference between two dates
  getDaysDifference(date1, date2) {
    const d1 = new Date(date1);
    const d2 = new Date(date2);
    const diffTime = Math.abs(d2 - d1);
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  }

  // Shuffle array (Fisher-Yates algorithm)
  shuffleArray(array) {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  }

  // Get progress statistics
  getProgressStats(progress) {
    const calendarData = progress.calendarData || {};
    const completedDates = Object.keys(calendarData).filter(date => {
      const data = calendarData[date];
      return data.status === 'DS' || data.status === 'DF';
    });
    
    const successfulDates = Object.keys(calendarData).filter(date => {
      const data = calendarData[date];
      return data.status === 'DS';
    });
    
    const totalCompleted = completedDates.length;
    const totalSuccessful = successfulDates.length;
    const successRate = totalCompleted > 0 ? (totalSuccessful / totalCompleted) * 100 : 0;
    
    // Calculate current streak
    const today = new Date().toISOString().split('T')[0];
    let currentStreak = 0;
    let checkDate = new Date(today);
    
    while (true) {
      const dateString = checkDate.toISOString().split('T')[0];
      const dayData = calendarData[dateString];
      
      if (!dayData || (dayData.status !== 'DS' && dayData.status !== 'DF')) {
        break;
      }
      
      currentStreak++;
      checkDate.setDate(checkDate.getDate() - 1);
    }
    
    return {
      totalCompleted,
      totalSuccessful,
      successRate: Math.round(successRate),
      currentStreak,
      totalSets: this.totalSets,
      remainingSets: this.totalSets - totalCompleted
    };
  }

  // Get recommended sets for today
  async getRecommendedSets(userSettings) {
    const stats = this.getProgressStats(userSettings.progress);
    const { dailyGoal } = userSettings.learningSchedule;
    
    // If user is behind schedule, recommend more sets
    const recommendedGoal = Math.min(dailyGoal + 1, 3);
    
    return await this.getTodaySets({
      ...userSettings,
      learningSchedule: {
        ...userSettings.learningSchedule,
        dailyGoal: recommendedGoal
      }
    });
  }
}

// Create singleton instance
const learningScheduler = new LearningScheduler();

export default learningScheduler;
